import React from 'react';
import {
  buildModuleSplitRow,
  ModuleBreakdown,
  ModuleSplitLine,
} from '../../utils/pricingSheetData';
import { BuildupStep, buildupLines } from '../../utils/internalSplitBuildup';

// Read-only inline price breakdown for the selected module, drawn straight from
// the last Calculate Price result. It reuses the same per-mill grouping
// (buildModuleSplitRow) and per-line build-up (buildupLines) the View Split
// modal and the internal PDF use, so the on-screen numbers match the PDF.

const PANEL_TITLE = 'Price breakdown';
const HINT_TEXT = 'Click Calculate Price to see the breakdown.';
const MODULE_TOTAL_LABEL = 'Module Total';

const formatCurrency = (n: number): string =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PanelShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="border-t border-[#E2E5EA] pt-5">
    <h3 className="text-base font-medium text-blueprint-blue mb-3">{PANEL_TITLE}</h3>
    {children}
  </div>
);

// The itemised cost-to-price build-up for an NRE or per-pipe line: one row per
// component, then the cost subtotal and the margin step to the price. The cost
// and price rows get a rule above them and bolder text so the eye lands there.
const BuildupTable: React.FC<{ steps: BuildupStep[] }> = ({ steps }) => (
  <div className="pl-5 mt-1.5 mb-1 space-y-px">
    {steps.map((step, i) => {
      const emphasised = step.kind === 'cost' || step.kind === 'price';
      return (
        <div
          key={i}
          className={`grid grid-cols-[6.5rem_1fr_auto] gap-x-3 text-xs ${
            emphasised
              ? 'mt-1 border-t border-[#E2E5EA] pt-1 font-medium text-text-grey'
              : 'text-heritage-grey'
          }`}
        >
          <span>{step.label}</span>
          <span className="text-heritage-grey">{step.calc ?? ''}</span>
          <span className="text-right tabular-nums">{step.value}</span>
        </div>
      );
    })}
  </div>
);

// The cost-to-price build-up rows for one priced line (e.g. cost/price/margin
// for a tracking charge), rendered from the same pure helper the PDF uses.
const LineBuildup: React.FC<{ detail?: Record<string, unknown> }> = ({ detail }) => {
  const lines = buildupLines(detail);
  if (lines.length === 0) return null;
  return (
    <div className="pl-5 mt-1 space-y-0.5">
      {lines.map((line, i) => (
        <div key={i} className="flex text-xs text-heritage-grey">
          <span className="w-32 flex-shrink-0">{line.label}</span>
          <span>{line.value}</span>
        </div>
      ))}
    </div>
  );
};

const PricedLine: React.FC<{ line: ModuleSplitLine }> = ({ line }) => (
  <div>
    <div className="flex justify-between gap-4 pl-2 text-sm text-text-grey">
      <span>{line.label}</span>
      <span className="font-medium text-text-grey whitespace-nowrap">
        {formatCurrency(line.amount)}
      </span>
    </div>
    {line.buildupSteps && line.buildupSteps.length > 0 ? (
      <BuildupTable steps={line.buildupSteps} />
    ) : (
      <LineBuildup detail={line.detail} />
    )}
  </div>
);

interface Props {
  productId: string;
  breakdown?: ModuleBreakdown | null;
}

export const ModulePriceBreakdownPanel: React.FC<Props> = ({ productId, breakdown }) => {
  const row = buildModuleSplitRow(productId, breakdown);

  if (!row) {
    return (
      <PanelShell>
        <p className="text-sm text-heritage-grey">{HINT_TEXT}</p>
      </PanelShell>
    );
  }

  return (
    <PanelShell>
      <div className="rounded-xl border border-[#E2E5EA] bg-page-bg p-4 space-y-4">
        {row.mills.map((mill, mi) => (
          <div key={mi} className="space-y-2">
            <p className="text-sm font-semibold text-text-grey">{mill.name}</p>
            {mill.lines.map((line, li) => (
              <PricedLine key={li} line={line} />
            ))}
          </div>
        ))}

        {row.other.length > 0 && (
          <div className="space-y-2 border-t border-[#E2E5EA] pt-3">
            {row.other.map((line, i) => (
              <PricedLine key={i} line={line} />
            ))}
          </div>
        )}

        <div className="flex justify-between gap-4 border-t-2 border-[#E2E5EA] pt-3 text-sm font-semibold text-text-grey">
          <span>{MODULE_TOTAL_LABEL}</span>
          <span className="whitespace-nowrap">{formatCurrency(row.total)}</span>
        </div>
      </div>
    </PanelShell>
  );
};
